import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiUser } from 'react-icons/fi';
import { usePaiement } from '../hooks/usePaiements';
import { API_BASE_URL } from '../config/api';
import '../styles/PaiementDetail.css';

const PaiementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('information');
  const [internalNote, setInternalNote] = useState('');

  const { 
    paiement, 
    loading, 
    error, 
    updateNote, 
    refund 
  } = usePaiement(id);

  const handleSaveNote = async () => {
    try {
      await updateNote(internalNote);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleRefund = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir rembourser cette transaction ?')) {
      try {
        await refund();
      } catch (error) {
        console.error('Erreur lors du remboursement:', error);
      }
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${API_BASE_URL}/paiements/${id}/pdf`, '_blank');
  };

  const handleContactClient = () => {
    navigate(`/clients/${paiement.client?.id}`);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return <div className="error">Erreur: {error}</div>;
  }

  if (!paiement) {
    return <div className="error">Paiement non trouvé</div>;
  }

  return (
    <div className="paiement-detail">
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate('/paiements')}>
          <FiArrowLeft />
        </button>
        <h1>Fiche détails transaction</h1>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={handleRefund}>
            Relancer
          </button>
          <button className="btn btn-blue" onClick={handleDownloadPDF}>
            Télécharger reçu PDF
          </button>
          <button className="btn btn-blue" onClick={handleContactClient}>
            Contacter client
          </button>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-sidebar">
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'information' ? 'active' : ''}`}
              onClick={() => setActiveTab('information')}
            >
              Information générale
            </button>
            <button 
              className={`tab-button ${activeTab === 'donnees' ? 'active' : ''}`}
              onClick={() => setActiveTab('donnees')}
            >
              Données liées
            </button>
            <button 
              className={`tab-button ${activeTab === 'participants' ? 'active' : ''}`}
              onClick={() => setActiveTab('participants')}
            >
              Participants
            </button>
            <button 
              className={`tab-button ${activeTab === 'note' ? 'active' : ''}`}
              onClick={() => setActiveTab('note')}
            >
              Note interne
            </button>
          </div>
        </div>

        <div className="detail-main">
          {activeTab === 'information' && (
            <div className="info-tab">
              <div className="transaction-header">
                <h2># {paiement.reference}</h2>
                <div className="transaction-details">
                  <div className="amount-info">
                    <div className="amount">{formatAmount(paiement.amount)} CFA</div>
                    <div className="status success">Réussi</div>
                  </div>
                  <div className="service-info">
                    <div className="service-type">{paiement.service} (ID#{paiement.serviceId})</div>
                    <div className="date-time">
                      <span>{formatDate(paiement.date)}</span>
                      <span>{paiement.time}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="transaction-info">
                <div className="info-section">
                  <h3>Type</h3>
                  <div className="info-value">{paiement.type}</div>
                </div>

                <div className="info-section">
                  <h3>Cible du signalement</h3>
                  <div className="info-value">Croisé</div>
                </div>

                <div className="commission-section">
                  <h3>Commission prélevé</h3>
                  <div className="commission-details">
                    <span className="commission-amount">{formatAmount(paiement.commission)} CFA</span>
                    <span className="commission-rate">{paiement.commissionRate}%</span>
                  </div>
                  
                  <h3>Montant net versé au prestataire</h3>
                  <div className="net-amount">{formatAmount(paiement.montantPrestataire)} CFA</div>
                </div>

                <div className="payment-method-section">
                  <span className="method-badge">{paiement.paymentMethod}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'donnees' && (
            <div className="donnees-tab">
              <div className="info-section">
                <h3>{formatAmount(paiement.amount)} CFA</h3>
                <div className="info-value">{paiement.type}</div>
                
                <div className="commission-details">
                  <div className="label">Commission prélevé</div>
                  <div className="commission-amount">{formatAmount(paiement.commission)} CFA | {paiement.commissionRate}%</div>
                </div>
                
                <div className="net-section">
                  <div className="label">Montant net versé au prestataire</div>
                  <div className="net-amount">{formatAmount(paiement.montantPrestataire)} CFA</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'participants' && (
            <div className="participants-tab">
              <div className="participant-card">
                <div className="participant-header">
                  <img 
                    src={paiement.client?.avatar || '/default-avatar.png'} 
                    alt="Client" 
                    className="participant-avatar"
                  />
                  <div className="participant-info">
                    <h3>{paiement.client?.name}</h3>
                    <span className="participant-type">{paiement.client?.type}</span>
                  </div>
                </div>
                <button 
                  className="detail-link"
                  onClick={() => navigate(`/clients/${paiement.client?.id}`)}
                >
                  <FiUser /> Fiche détail
                </button>
              </div>

              <div className="participant-card">
                <div className="participant-header">
                  <img 
                    src={paiement.prestataire?.avatar || '/default-avatar.png'} 
                    alt="Prestataire" 
                    className="participant-avatar"
                  />
                  <div className="participant-info">
                    <h3>{paiement.prestataire?.name}</h3>
                    <span className="participant-type">{paiement.prestataire?.type}</span>
                  </div>
                </div>
                <button 
                  className="detail-link"
                  onClick={() => navigate(`/prestataires/${paiement.prestataire?.id}`)}
                >
                  <FiUser /> Fiche détail
                </button>
                <div className="mission-info">ID Mission : {paiement.missionId}</div>
              </div>
            </div>
          )}

          {activeTab === 'note' && (
            <div className="note-tab">
              <div className="note-editor">
                <h3>Note interne</h3>
                <div className="editor-toolbar">
                  <button type="button"><strong>B</strong></button>
                  <button type="button"><em>I</em></button>
                  <button type="button"><u>U</u></button>
                  <button type="button">•</button>
                  <button type="button">1.</button>
                  <div className="spacer"></div>
                  <select>
                    <option>Paragraph text</option>
                  </select>
                  <select>
                    <option>Arial</option>
                  </select>
                  <div className="spacer"></div>
                  <button type="button">⤴</button>
                  <button type="button">⤵</button>
                  <span>100%</span>
                  <span>00</span>
                  <button type="button">+</button>
                </div>
                <textarea
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Ajouter une note interne..."
                  rows="10"
                  className="note-textarea"
                />
                <div className="word-count">0 mots</div>
                <button className="btn btn-blue" onClick={handleSaveNote}>
                  Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaiementDetail;